import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';
import { minimatch } from 'minimatch';

import { CombinedErrorInternalObject } from '../prostudio.api-service.types';
import {
  ProStudioEvents,
  ProStudioContext,
  BotVersionFilesPattern,
} from '../prostudio.xstate.types';
import { checkForErrors, downloadZip } from '../../utils';

// customerName.botName.botVersion/
// ├── bot.zip
// ├── app.py
// ├── dist/
// │   └── requirements.json
// ├── stopwords.json
// ├── templates/
// |   └── bot.csv
// └── services/
//     └── *.py

const patternsForSearch: BotVersionFilesPattern[] = [
  'dist/requirements.json',
  'stopwords.json',
  'templates/bot.csv',
  'services/*.py',
];

export const downloadVersionFile = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<void> => {
  if (event.type !== 'getVersionFile') {
    return;
  }

  const { botName, customerId, project } = ctx;

  const projectId = project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  if (!botName) {
    throwErrorWithId('Bot Name is required');
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
  }

  const botId = `${customerId}.${botName}`;

  if (!event.version) {
    throwErrorWithId('Version is required');
  }

  const { version, files = 'all' } = event;

  let patterns: BotVersionFilesPattern[] = patternsForSearch;

  patterns = !files || files === 'all' ? patternsForSearch : files;

  const versionId = `${botId}.${version}`;

  const versionDataResponse = await urqlGql.getVersionById({
    versionId,
    projectId,
  });

  checkForErrors(versionDataResponse);

  const existingFiles: string[] =
    versionDataResponse.data?.getVersionById?.data.files || [];

  const filePathesToDownload = existingFiles.filter((file) =>
    patterns.some((pattern) => minimatch(file, pattern))
  );

  const downloadedfiles: { data: string; name: string }[] = [];

  await Promise.all(
    filePathesToDownload.map(async (file) => {
      const versionFileResponse = await urqlGql.getVersionFile({
        projectId,
        versionId,
        filePath: file,
      });

      const fileData = (
        versionFileResponse.error?.graphQLErrors[0]?.extensions
          ?.internal as CombinedErrorInternalObject
      )?.response.body.data.file_data;

      downloadedfiles.push({ data: fileData, name: file });
    })
  );

  await downloadZip(versionId, downloadedfiles);
};
